import type { PostalCodeDto } from '@wanda-space/ops-types'
import {
  type ConditionType,
  FeatureFlagResponseDto,
  type SupportedCities,
  type SupportedCountries,
} from '@wanda-space/types'
import type { FlattenedDeliveryInfo } from 'interfaces'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import * as Yup from 'yup'

import type { FormatMessage } from './format'

export const validatePostalCode = (
  searchCode: string,
  postalCodes: PostalCodeDto[],
  countryCode: SupportedCountries,
  city: SupportedCities
) =>
  postalCodes.some(
    (pc) => pc.postalCode === searchCode && pc.country === countryCode && pc.city === city
  )

export const validateConditionType = (condition: string, conditionType: ConditionType) =>
  Object.keys(conditionType).some((c) => c === condition)

export const requiredString = (formatMessage: FormatMessage) =>
  Yup.string().required(formatMessage({ id: 'validation.required' }))
export const email = (formatMessage: FormatMessage) =>
  Yup.string()
    .email(formatMessage({ id: 'validation.invalidEmail' }))
    .required(formatMessage({ id: 'validation.required' }))

export const phoneNumber = (formatMessage: FormatMessage) => {
  return Yup.string()
    .matches(
      /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      formatMessage({ id: 'validation.phonenumber' })
    )
    .test('is-valid-phoneNumber', formatMessage({ id: 'validation.phonenumber' }), (value) => {
      if (value) {
        // used by isPhoneNumber decorator in howls-castle
        return Boolean(parsePhoneNumberFromString(value)?.isValid())
      }
      return true
    })
}

export const firstName = (formatMessage: FormatMessage) => {
  return requiredString(formatMessage)
    .min(1, formatMessage({ id: 'validation.minLengthLetters' }, { length: 1 }))
    .max(50, formatMessage({ id: 'validation.maxLengthLetters' }, { length: 50 }))
}

export const lastName = (formatMessage: FormatMessage) => {
  return requiredString(formatMessage)
    .min(1, formatMessage({ id: 'validation.minLengthLetters' }, { length: 1 }))
    .max(50, formatMessage({ id: 'validation.maxLengthLetters' }, { length: 50 }))
}
export const floorNumber = (formatMessage: FormatMessage) =>
  Yup.number()
    .min(-10)
    .max(200)
    .required(formatMessage({ id: 'validation.required' }))

export const companyName = (formatMessage: FormatMessage) =>
  Yup.string()
    .nullable()
    .test(
      'companyName-required',
      formatMessage({ id: 'validation.required.both.company-name.org-number' }),
      function (value) {
        const { orgNumber } = this.parent
        if (!value && orgNumber) {
          return this.createError({
            path: 'companyName',
            message: formatMessage({ id: 'validation.required.both.company-name.org-number' }),
          })
        }
        return true
      }
    )

export const orgNumber = (formatMessage: FormatMessage) =>
  Yup.string()
    .nullable()
    .test(
      'orgNumber-required',
      'Both companyName and orgNumber must be filled if one is provided',
      function (value) {
        const { companyName } = this.parent
        if (!value && companyName) {
          return this.createError({
            path: 'orgNumber',
            message: formatMessage({ id: 'validation.required.both.company-name.org-number' }),
          })
        }
        return true
      }
    )

export const authSchema = (formatMessage: FormatMessage) =>
  Yup.object().shape({
    email: email(formatMessage),
    password: requiredString(formatMessage).min(
      8,
      formatMessage({ id: 'validation.minLengthLetters' }, { length: 8 })
    ),
  })

export const ResetPasswordSchema = (formatMessage: FormatMessage) =>
  Yup.object().shape({
    email: email(formatMessage),
  })

export const ProfileSchema = (formatMessage: FormatMessage) =>
  Yup.object().shape({
    firstName: requiredString(formatMessage)
      .min(1, formatMessage({ id: 'validation.minLengthLetters' }, { length: 1 }))
      .max(50, formatMessage({ id: 'validation.maxLengthLetters' }, { length: 50 })),
    lastName: requiredString(formatMessage)
      .min(1, formatMessage({ id: 'validation.minLengthLetters' }, { length: 1 }))
      .max(50, formatMessage({ id: 'validation.maxLengthLetters' }, { length: 50 })),
    companyName: Yup.string()
      .min(1, formatMessage({ id: 'validation.minLengthLetters' }, { length: 1 }))
      .max(50, formatMessage({ id: 'validation.maxLengthLetters' }, { length: 50 })),
  })

export const PhoneNumberSchema = (formatMessage: FormatMessage) =>
  Yup.object().shape({
    phoneNumber: phoneNumber(formatMessage),
  })

export const ChangePasswordSchema = (formatMessage: FormatMessage) =>
  Yup.object().shape({
    password: requiredString(formatMessage).min(
      6,
      formatMessage({ id: 'validation.minLengthLetters' }, { length: 6 })
    ),
    confirmNewPassword: requiredString(formatMessage).oneOf(
      [Yup.ref('password')],
      formatMessage({ id: 'validation.passwordDontMatch' })
    ),
  })

export const ListingDimensionFieldsSchema = (formatMessage: FormatMessage) => {
  return Yup.object().shape({
    dimensionLength: Yup.number()
      .min(1, formatMessage({ id: 'validation.minimum1' }))
      .optional(),
    dimensionWidth: Yup.number()
      .min(1, formatMessage({ id: 'validation.minimum1' }))
      .optional(),
    dimensionHeight: Yup.number()
      .min(1, formatMessage({ id: 'validation.minimum1' }))
      .optional(),
  })
}

export const ListingExtraDescriptiveInfoFieldsSchema = () => {
  return Yup.object().shape({
    brand: Yup.string().optional(),
    model: Yup.string().optional(),
    material: Yup.string().optional(),
    year: Yup.string().optional(),
    colour: Yup.string().optional(),
    comment: Yup.string().optional(),
  })
}

export const ContactPersonSchema = (formatMessage: FormatMessage) =>
  Yup.object().shape({
    name: Yup.string(),
    phoneNumber: phoneNumber(formatMessage),
  }) as Yup.Schema<unknown, unknown, any, ''>

export const validateDeliveryInfo = (address: FlattenedDeliveryInfo) =>
  Boolean(address.street && address.postalCode && address.city)

export const BidListingSchema = (formatMessage: FormatMessage) =>
  Yup.object().shape({
    amount: Yup.number().required(formatMessage({ id: 'validation.required' })),
  })
